import type { CasOrCategory, EmissionsPeriod, EmissionsUnitID, ProductID, ReleaseProfileID } from '../types/db.ts';
import type { FormulaID, OutputID, WasteStreamID } from '../types/release-profiles.ts';
import type { TransferID } from '../types/questionnaires.ts';
import type { CalculationLine } from './releases-calculate.ts';
import { periodToLabel } from './emissions/emissions-dates.ts';

export type ThresholdCalculationLine = CalculationLine<CasOrCategory>;

/**
 * Everything that identifies "the same calculation" across two reporting years.
 *
 * Deliberately excludes the emissions period. An emissions unit produces one calculation line per tracking period, so
 * a monthly unit contributes twelve lines to a year's threshold and `2025-03` has no counterpart in `2024-03`. Periods
 * are therefore summed away into a single comparable figure per year, and the underlying lines are kept on the group
 * so a drilldown can still show the period-by-period detail.
 */
export interface CalculationLineIdentity {
  emissionsUnitID: EmissionsUnitID | null;
  releaseProfileID: ReleaseProfileID | null;
  releaseProfileFormulaID: FormulaID | null;
  releaseProfileOutputID: OutputID | null;
  releaseProfileFormulaName: string;
  releaseProfileProcessName: string;
  transferID: TransferID;
  wasteStreamID: WasteStreamID;
  productID: ProductID | null;
  casOrCategoryOriginal: CasOrCategory;
}

/** Labels for an identity. Every line sharing an identity shares these, so either year's lines can supply them. */
export interface CalculationLineLabels {
  releaseProfileName: string;
  releaseProfileFormulaName: string;
  releaseProfileProcessName: string;
  productName: string | null;
}

/**
 * A year's worth of lines for one identity, collapsed to a single figure.
 *
 * Summing happens in the lines' own computation units rather than in pounds, so that what a drilldown displays matches
 * what the formulas produced. Threshold totals themselves are converted to pounds/grams separately by
 * `triReleasesCalculate`, which is why a mismatch here is reported rather than normalized away: it means one formula
 * produced different units in different periods, which is a data problem a user should see instead of a summed number
 * that quietly means nothing.
 */
export type CalculationLineTotal =
  | { status: 'summed'; total: number; units: string }
  | { status: 'unitsMismatched'; unitsFound: string[] };

export interface CalculationLineYear {
  /** Sorted by period, earliest first. Non-emissions identities have exactly one line here. */
  lines: ThresholdCalculationLine[];
  total: CalculationLineTotal;
}

export type CalculationLineChange =
  | {
      status: 'comparable';
      delta: number;
      units: string;
      /** Null when the prior year total was zero, as there is no meaningful percentage to report. */
      percent: number | null;
    }
  | { status: 'notComparable'; reason: string };

export type CalculationLineComparison = {
  key: string;
  identity: CalculationLineIdentity;
  labels: CalculationLineLabels;
} & (
  | { presence: 'both'; current: CalculationLineYear; prior: CalculationLineYear; change: CalculationLineChange }
  | { presence: 'currentOnly'; current: CalculationLineYear }
  | { presence: 'priorOnly'; prior: CalculationLineYear }
);

export function calculationLineIdentity(line: ThresholdCalculationLine): CalculationLineIdentity {
  return {
    emissionsUnitID: line.emissionsUnit?.emissionsUnitID ?? null,
    releaseProfileID: line.releaseProfileID,
    releaseProfileFormulaID: line.releaseProfileFormulaID,
    releaseProfileOutputID: line.releaseProfileOutputID,
    releaseProfileFormulaName: line.releaseProfileFormulaName,
    releaseProfileProcessName: line.releaseProfileProcessName,
    transferID: line.transferID,
    wasteStreamID: line.wasteStreamID,
    productID: line.product?.productID ?? null,
    casOrCategoryOriginal: line.casOrCategoryOriginal,
  };
}

/**
 * A stable string form of an identity. JSON of a fixed-order array rather than a delimiter-joined string, because
 * profile, formula and process names are user-supplied and would otherwise be able to collide across fields.
 */
export function calculationLineIdentityKey(identity: CalculationLineIdentity): string {
  return JSON.stringify([
    identity.emissionsUnitID,
    identity.releaseProfileID,
    identity.releaseProfileFormulaID,
    identity.releaseProfileOutputID,
    identity.releaseProfileFormulaName,
    identity.releaseProfileProcessName,
    identity.transferID,
    identity.wasteStreamID,
    identity.productID,
    identity.casOrCategoryOriginal,
  ]);
}

function periodSortKey(line: ThresholdCalculationLine): string {
  const period = line.emissionsUnit?.period ?? null;
  if (period === null) {
    return '';
  }

  // Fall back to the raw key when we cannot resolve a start date, so that ordering stays stable rather than arbitrary:
  return periodToLabel(period).start ?? period;
}

function calculationLineYearBuild(lines: ThresholdCalculationLine[]): CalculationLineYear {
  const linesSorted = lines.slice().sort((a, b) => {
    const periodCompare = periodSortKey(a).localeCompare(periodSortKey(b));
    return periodCompare === 0 ? a.lineID - b.lineID : periodCompare;
  });

  const unitsFound: string[] = [];
  for (const line of linesSorted) {
    if (!unitsFound.includes(line.computationUnits)) {
      unitsFound.push(line.computationUnits);
    }
  }

  if (unitsFound.length > 1) {
    return { lines: linesSorted, total: { status: 'unitsMismatched', unitsFound } };
  }

  let total = 0;
  for (const line of linesSorted) {
    total += line.computationResult;
  }

  return { lines: linesSorted, total: { status: 'summed', total, units: unitsFound[0] ?? '' } };
}

function calculationLineChangeBuild(current: CalculationLineYear, prior: CalculationLineYear): CalculationLineChange {
  if (current.total.status !== 'summed' || prior.total.status !== 'summed') {
    return { status: 'notComparable', reason: 'One of the years produced more than one unit of measure.' };
  }

  if (current.total.units !== prior.total.units) {
    return {
      status: 'notComparable',
      reason: `This year produced ${current.total.units || 'no units'} but last year produced ${prior.total.units || 'no units'}.`,
    };
  }

  return {
    status: 'comparable',
    delta: current.total.total - prior.total.total,
    units: current.total.units,
    percent: prior.total.total === 0 ? null : ((current.total.total - prior.total.total) / prior.total.total) * 100,
  };
}

function calculationLineMagnitude(comparison: CalculationLineComparison): number {
  const year = comparison.presence === 'priorOnly' ? comparison.prior : comparison.current;
  return year.total.status === 'summed' ? Math.abs(year.total.total) : Infinity;
}

const presenceOrder: Record<CalculationLineComparison['presence'], number> = {
  both: 0,
  currentOnly: 1,
  priorOnly: 2,
};

/**
 * Pairs up a chemical's calculation lines from two reporting years so they can be shown side by side.
 *
 * Returns comparable pairs first, then lines only present in the current year, then lines only present in the prior
 * year; within each of those, largest contribution first.
 */
export function calculationLinesCompareYears(
  currentLines: ThresholdCalculationLine[],
  priorLines: ThresholdCalculationLine[],
): CalculationLineComparison[] {
  const grouped = new Map<
    string,
    { identity: CalculationLineIdentity; current: ThresholdCalculationLine[]; prior: ThresholdCalculationLine[] }
  >();

  const collect = (lines: ThresholdCalculationLine[], year: 'current' | 'prior') => {
    for (const line of lines) {
      const identity = calculationLineIdentity(line);
      const key = calculationLineIdentityKey(identity);
      let group = grouped.get(key);
      if (!group) {
        group = { identity, current: [], prior: [] };
        grouped.set(key, group);
      }

      group[year].push(line);
    }
  };

  collect(currentLines, 'current');
  collect(priorLines, 'prior');

  const result: CalculationLineComparison[] = [];
  for (const [key, group] of grouped) {
    const labelSource = group.current[0] ?? group.prior[0]!;
    const labels: CalculationLineLabels = {
      releaseProfileName: labelSource.releaseProfileName,
      releaseProfileFormulaName: labelSource.releaseProfileFormulaName,
      releaseProfileProcessName: labelSource.releaseProfileProcessName,
      productName: labelSource.product?.productName ?? null,
    };

    if (group.current.length > 0 && group.prior.length > 0) {
      const current = calculationLineYearBuild(group.current);
      const prior = calculationLineYearBuild(group.prior);
      result.push({
        key,
        identity: group.identity,
        labels,
        presence: 'both',
        current,
        prior,
        change: calculationLineChangeBuild(current, prior),
      });
    } else if (group.current.length > 0) {
      result.push({
        key,
        identity: group.identity,
        labels,
        presence: 'currentOnly',
        current: calculationLineYearBuild(group.current),
      });
    } else {
      result.push({
        key,
        identity: group.identity,
        labels,
        presence: 'priorOnly',
        prior: calculationLineYearBuild(group.prior),
      });
    }
  }

  return result.sort((a, b) => {
    const presenceCompare = presenceOrder[a.presence] - presenceOrder[b.presence];
    if (presenceCompare !== 0) {
      return presenceCompare;
    }

    const magnitudeCompare = calculationLineMagnitude(b) - calculationLineMagnitude(a);
    if (magnitudeCompare !== 0) {
      return magnitudeCompare;
    }

    return a.key.localeCompare(b.key);
  });
}

/** The display label for the emissions period a line was calculated for, or null when it is not emissions-derived. */
export function calculationLinePeriodFriendly(line: Pick<ThresholdCalculationLine, 'emissionsUnit'>): string | null {
  const period: EmissionsPeriod | 'potential' | undefined = line.emissionsUnit?.period;
  return period === undefined ? null : periodToLabel(period).friendly;
}
